/**
 * Prometheus Second Brain wrapper.
 * Loads workshop brain-notes and passes them as extraNodes into the shared studio.
 */
import React, { useEffect, useState } from "react";
import {
  SecondBrain3DStudio as SharedSecondBrain3DStudio,
  type DetailedNode,
  type BrainDomain,
  type LiveMemoryGraphData,
} from "@shared/second-brain/SecondBrain3DStudio";
import { getBaseUrl } from "@/lib/apiClient";

export type { BrainDomain, DetailedNode, LiveMemoryGraphData, SourceGroup } from "@shared/second-brain/SecondBrain3DStudio";
export { BASE_AURA_SOURCES, mapCategoryToAuraSource } from "@shared/second-brain/SecondBrain3DStudio";

interface Props {
  initialDomain?: BrainDomain;
  liveData?: LiveMemoryGraphData;
}

async function loadWorkshopNotes(): Promise<DetailedNode[]> {
  let localNotes: any[] = [];
  try {
    localNotes = JSON.parse(localStorage.getItem("prometheus_saved_brain_nodes") || "[]");
  } catch {
    localNotes = [];
  }

  try {
    const res = await fetch(`${getBaseUrl()}/api/prometheus/brain-notes`);
    if (res.ok) {
      const data = await res.json();
      if (data.notes && Array.isArray(data.notes)) {
        const apiNodes: DetailedNode[] = data.notes.map((n: any) => {
          const cat = (n.category || "Proyectos").toLowerCase();
          let color = "#10b981";
          let cluster = "Proyectos de Taller";
          let sourceId = "projects";
          let sourceName = "Proyectos de Taller";

          if (cat === "perfil") {
            color = "#38bdf8";
            cluster = "Perfil Físico & Taller";
            sourceId = "wiki";
            sourceName = "Perfil Físico & Taller";
          } else if (cat === "decisiones") {
            color = "#f59e0b";
            cluster = "Decisiones Inmutables";
            sourceId = "memory";
            sourceName = "Bitácora & Decisiones";
          } else if (cat === "auditoria") {
            color = "#a855f7";
            cluster = "Auditoría AIS-OS";
            sourceId = "memory";
            sourceName = "Auditoría AIS-OS";
          }

          return {
            id: `p_file_${n.slug}`,
            title: n.title,
            sourceId,
            sourceName,
            color,
            x: 0,
            y: 0,
            z: 0,
            cluster,
            isKeyNode: true,
            isLiveSaved: true,
            filename: n.filename,
            description: `Archivo vivo en ${n.filename}`,
            content: n.content,
            created_at: n.updated_at,
            connections: ["p_anchor_projects", "p_anchor_memory"],
          };
        });

        const combined = [...localNotes];
        apiNodes.forEach((an) => {
          if (!combined.some((c: any) => c.filename === an.filename || c.title === an.title)) {
            combined.push(an);
          }
        });
        return combined as DetailedNode[];
      }
    }
  } catch {
    // fall through to local
  }
  return localNotes as DetailedNode[];
}

export const SecondBrain3DStudio: React.FC<Props> = ({
  initialDomain = "prometheus",
  liveData,
}) => {
  const [extraNodes, setExtraNodes] = useState<DetailedNode[]>([]);

  useEffect(() => {
    let cancelled = false;
    loadWorkshopNotes().then((notes) => {
      if (!cancelled) setExtraNodes(notes);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SharedSecondBrain3DStudio
      initialDomain={initialDomain}
      liveData={liveData}
      extraNodes={extraNodes}
    />
  );
};

export default SecondBrain3DStudio;
